import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { composeFilters } from '@iheartjane/menu';
import { useMobileMediaQuery } from '@iheartjane/reefer';
import isEqual from 'lodash-es/isEqual';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { useFacets } from '@jane/dm/internal';
import {
  useFormattedProductFilters,
  useSearchContext,
} from '@jane/search/hooks';
import type {
  AlgoliaFacets,
  ArrayFilterValue,
  FilterSortOption,
  MenuOptions,
  ProductFilterKeys,
  RangeFilterValue,
} from '@jane/search/types';
import { makeCurrentSort } from '@jane/search/types';
import {
  buildSearchFilter,
  formatActiveFilters,
  handleFilterChange,
  handleFilterDeselect,
  overrideCustomCategoryLabels,
  sortOptionsWithRecommended,
  updateFacetCounts,
  updateSortSuffix,
} from '@jane/search/util';
import {
  JaneGoldCallout,
  ProductFilterBar,
} from '@jane/shared-ecomm/components';
import {
  useAuthentication,
  useBrandSpecialsContext,
  useMenu,
  useSpecials,
  useUserPreferences,
} from '@jane/shared-ecomm/providers';
import { useShouldShowGold } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';
import type { MenuCategoryPath } from '@jane/shared/types';

const DEFAULT_GOLD_FACETS = { has_brand_discount: { true: 0 } };

interface MenuFiltersProps {
  isLoadingProducts?: boolean;
  menuCategory: MenuCategoryPath;
  noPadding?: boolean;
  searchResultFacets: SmartSort['searchFacets'];
  searchResultFacetsLoading: boolean;
  showOnlyWeights?: MenuOptions['showOnlyWeights'];
  staticFilters: string;
  totalResults: number;
}

export const MenuFilters = ({
  isLoadingProducts,
  menuCategory,
  noPadding = false,
  searchResultFacets,
  searchResultFacetsLoading,
  totalResults,
  showOnlyWeights,
  staticFilters,
}: MenuFiltersProps) => {
  const compareEnabled = useFlag(FLAGS.c1CompareProducts);
  const isMobile = useMobileMediaQuery({});
  const [facetsToRender, setFacetsToRender] = useState<Partial<AlgoliaFacets>>(
    {}
  );

  const {
    appInfo: { appMode, productReviewsEnabled },
    handleSetListView,
    listView,
    loading,
    store,
  } = useMenu();
  const {
    userLocation: { cityState },
  } = useUserPreferences();
  const { specials } = useSpecials();

  const { searchState, setSearchState } = useSearchContext();
  const { brandSpecialFilter, brandSpecials } = useBrandSpecialsContext();
  const { brand_special_id: brandSpecialId, special_id: specialId } =
    useParams();

  const shouldShowGold = useShouldShowGold({ store });

  const { currentSort, filters } = searchState;

  const specialsPage = menuCategory === 'specials';
  const brandSpecialPage = menuCategory === 'jane-gold';
  const brandSpecialDetailPage = brandSpecialPage && !!brandSpecialId;
  const categoryPage = !['all', 'featured', 'specials'].includes(menuCategory)
    ? menuCategory
    : undefined;
  const specialDetailPage = specialsPage && !!specialId;

  const onQuery = (value: string) => setSearchState({ searchText: value });

  const onSort = (value: FilterSortOption) => {
    const weightFilters = filters?.['available_weights'] as string[];
    const newSort = makeCurrentSort(value);
    newSort.suffix = updateSortSuffix(newSort.suffix, weightFilters);

    setSearchState({ currentSort: newSort });
  };

  const onChange = useCallback(
    (
      filterKey: ProductFilterKeys,
      filterValue: ArrayFilterValue | RangeFilterValue
    ) => {
      return setSearchState(
        handleFilterChange(filterKey, filterValue, searchState)
      );
    },
    [setSearchState, searchState]
  );

  const onDeselect = (filterKey: ProductFilterKeys) =>
    setSearchState(handleFilterDeselect(filterKey, searchState));

  const shouldOverride = Boolean(
    store?.state === 'California' || (cityState && cityState.includes(', CA'))
  );

  const hasBrandSpecials = brandSpecials?.length > 0;

  const menuOptions = {
    customLabels: {
      root_types: overrideCustomCategoryLabels(
        shouldOverride,
        store?.custom_product_type_labels
      ),
      category: store?.custom_lineage_labels,
    },
    customRanking: store?.custom_product_type_ranking,
    customRows: store?.custom_rows,
    disabledFilters: store?.disabled_feeling_and_activity_tags,
    hasBrandSpecials,
    menuTabs: store?.menu_tabs,
    showOnlyWeights,
    showRatings: productReviewsEnabled,
    specials: specials as StoreSpecial[],
    specialsLabel: store?.store_compliance_language_settings?.['specials'],
    storeId: store?.id,
    storeState: store?.state,
  };

  const appOptions = {
    appMode,
    brandSpecialPage,
    categoryPage,
    specialsPage,
  };

  const { facets, isLoading } = useFacets({
    searchFilter: staticFilters,
    searchQuery: searchState?.searchText ?? undefined,
    storeId: Number(store.id),
  });

  // The gold facets query should always be filtered by gold, however if we're filtering
  // on gold, the staticFilters will already include the gold filter and we don't need to
  // add it.
  const goldFilterValues = searchState.filters?.['has_brand_discount'] ?? [];
  const isFilteredByGold = goldFilterValues?.length > 0;

  const goldFacetsStaticFilters = composeFilters(
    staticFilters,
    !isFilteredByGold ? brandSpecialFilter : ''
  );

  // We don't want to fetch gold facets if gold facets don't exist at all
  // (i.e. a store does not have gold enabled, or has no gold products on the menu)
  const goldFacetsEnabled =
    shouldShowGold &&
    Boolean(
      facets &&
      facets['has_brand_discount'] &&
      facets['has_brand_discount']['true']
    );

  const goldSearchFilter = buildSearchFilter({
    searchState,
    staticFilters: goldFacetsStaticFilters,
  });

  /**
   * we make a separate fetch for gold facets to ensure the count is always correct
   * as its taking into account both applicable_brand_special_ids & has_brand_discount
   */
  const { facets: goldFacets, isLoading: goldFacetsFetching } = useFacets({
    searchFilter: goldSearchFilter,
    storeId: Number(store.id),
    enabled: Boolean(goldFacetsEnabled && hasBrandSpecials),
    searchQuery: searchState?.searchText ?? undefined,
    searchFacets: ['has_brand_discount'],
  });

  /**
   * this default ensures the count is updated correctly when there are no facets returned
   */
  const goldFacetsToRender =
    Object.values(goldFacets || {}).filter(Boolean).length > 0
      ? goldFacets
      : DEFAULT_GOLD_FACETS;

  /**
   * We get the special facet counts without specials filters applied
   *  to ensure the correct counts are shown since we treat the different special
   * facets as one (i.e. filtering by one should not reduce the counts of another)
   */
  const specialsSearchFilter = buildSearchFilter({
    searchState: {
      ...searchState,
      filters: {
        ...searchState.filters,
        applicable_bulk_special_ids: [],
        'applicable_bundle_special_ids.discount': [],
        'applicable_bundle_special_ids.qualify': [],
        applicable_cart_total_special_ids: [],
        applicable_special_ids: [],
      },
    },
    staticFilters,
  });

  const { facets: specialFacets, isLoading: specialFacetsLoading } = useFacets({
    searchFilter: specialsSearchFilter,
    searchQuery: searchState?.searchText ?? undefined,
    storeId: Number(store.id),
    searchFacets: [
      'applicable_bulk_special_ids',
      'applicable_bundle_special_ids.discount',
      'applicable_bundle_special_ids.qualify',
      'applicable_cart_total_special_ids',
      'applicable_special_ids',
    ],
  });

  useEffect(() => {
    if (
      searchResultFacetsLoading ||
      isLoading ||
      goldFacetsFetching ||
      specialFacetsLoading
    )
      return;

    /**
     * The specialFacets object only returns facets with a count greater than zero. In this case, we need to set
     * the counts for all special facets to zero before spreading the specialFacets to ensure that we don't use an
     * inaccurate count from the facets object.
     */
    const newFacets = updateFacetCounts(
      searchState,
      {
        ...facets,
        applicable_bulk_special_ids: {},
        'applicable_bundle_special_ids.discount': {},
        'applicable_bundle_special_ids.qualify': {},
        applicable_cart_total_special_ids: {},
        applicable_special_ids: {},
        ...specialFacets,
        ...goldFacetsToRender,
      },
      searchResultFacets ?? {}
    );

    if (!isEqual(newFacets, facetsToRender)) {
      setFacetsToRender(newFacets);
    }
  }, [
    facets,
    isLoading,
    searchResultFacets,
    searchResultFacetsLoading,
    goldFacets,
    goldFacetsFetching,
    searchState,
    specialFacets,
    specialFacetsLoading,
    facetsToRender,
  ]);

  const activeFilters = formatActiveFilters(searchState, menuOptions);
  const productFilters = useFormattedProductFilters({
    appOptions,
    facets: facetsToRender,
    menuOptions,
  });

  const {
    defaultLogin,
    errors,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  } = useAuthentication();

  const authentication = {
    errors,
    login: defaultLogin,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  };

  const showGoldCallout =
    shouldShowGold &&
    store.state === 'Ohio' &&
    activeFilters.some(
      (filter) => filter.key === 'has_brand_discount' && filter.value === 'true'
    );

  return (
    <>
      <ProductFilterBar
        activeFilters={activeFilters}
        currentSort={currentSort}
        condensedView={brandSpecialDetailPage || specialDetailPage}
        filters={productFilters}
        isLoading={isLoadingProducts}
        isLoadingFacets={!facets || loading || isLoading}
        listView={listView}
        onChange={(filterKey, value) => {
          onChange(filterKey as ProductFilterKeys, value);
        }}
        onDeselect={(filterKey) => {
          onDeselect(filterKey as ProductFilterKeys);
        }}
        onQuery={onQuery}
        onSort={onSort}
        onView={handleSetListView}
        modalSearchState={searchState}
        noPadding={noPadding}
        sortOptions={sortOptionsWithRecommended}
        searchId={store?.id}
        setModalSearchState={setSearchState}
        showCompare={compareEnabled}
        totalResults={totalResults}
        variant="product"
      />
      {showGoldCallout && (
        <JaneGoldCallout
          authentication={authentication}
          storeState={store.state}
          variant="goldRewards"
          mx={isMobile ? 24 : 64}
          mb={24}
          mt={8}
        />
      )}
    </>
  );
};
